import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosInstance } from 'axios';
import { confirmLink, confirmRelink, getLinkReview, getRelinkReview, Issue, LinkReview, RelinkAccount, RelinkChargebeeCustomer, RelinkReview } from '../../api/issues';
import { describeError } from '../../api/requeue';
import {
    CompareField,
    LINK_OUTCOME,
    linkableEntry,
    linkFields,
    money,
    RELINK_OUTCOME,
    rankedCandidates,
    relinkFields,
    suggestedAccount,
} from '../../components/Issues/exactCompare';
import { EnvChip, Loading, Mark, Spinner } from './ui';
import { EnvKind } from '../data/cockpit';

interface DialogProps {
    axios: AxiosInstance;
    issue: Issue;
    kind: EnvKind;
    onClose: () => void;
    /** Called after the change went through, to reload the panel. */
    onDone: () => void;
}

/** The frame both dialogs share: title with the environment, body, and the buttons at the bottom. */
const Frame = ({ title, issue, kind, onClose, children, footer }: { title: string; issue: Issue; kind: EnvKind; onClose: () => void; children: ReactNode; footer: ReactNode }) => (
    <>
        <button className="scrim dialog-scrim" aria-label="Annuleren" onClick={onClose} />
        <div className="dialog wide" role="dialog" aria-label={title}>
            <div className="ch">
                <span className="h2">{title}</span>
                <EnvChip kind={kind} environment={`${issue.customer} ${issue.environment}`} />
            </div>
            <div className="cb xc">{children}</div>
            <div className="dfoot">{footer}</div>
        </div>
    </>
);

/** Where it comes from and what it is tied to now, side by side. */
const Route = ({ from, to }: { from: { label: string; title: ReactNode; sub?: ReactNode }; to: { label: string; title: ReactNode; sub?: ReactNode; gone?: boolean } }) => (
    <div className="xc-route">
        <div className="xc-party">
            <span className="systag cb">CB</span>
            <div>
                <div className="eyebrow">{from.label}</div>
                <strong>{from.title}</strong>
                {from.sub && <div className="mono mut">{from.sub}</div>}
            </div>
        </div>
        <span className="xc-arrow" aria-hidden="true">→</span>
        <div className={`xc-party${to.gone ? ' gone' : ''}`}>
            <span className="systag ex">EX</span>
            <div>
                <div className="eyebrow">{to.label}</div>
                <strong>{to.title}</strong>
                {to.sub && <div className="mut">{to.sub}</div>}
            </div>
        </div>
    </div>
);

/** Chargebee and Exact field by field; green where they agree, amber where they differ. */
const CompareTable = ({ exactLabel, fields }: { exactLabel: string; fields: CompareField[] }) => (
    <table className="tbl xc-tbl">
        <thead>
            <tr>
                <th aria-label="Veld" />
                <th>Chargebee</th>
                <th>{exactLabel}</th>
                <th aria-label="Overeenkomst" />
            </tr>
        </thead>
        <tbody>
            {fields.map((f) => (
                <tr key={f.label} className={f.match === true ? 'same' : f.match === false ? 'differs' : undefined}>
                    <th>{f.label}</th>
                    <td>{f.chargebee || <span className="mut">–</span>}</td>
                    <td>{f.exact || <span className="mut">–</span>}</td>
                    <td className="verdict">
                        {f.match === true && <Mark tone="ok" />}
                        {f.match === false && <Mark tone="warn" />}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const Verify = ({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) => (
    <label className="xc-verify">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{children}</span>
    </label>
);

const Done = ({ text }: { text: string }) => (
    <div className="xc-note ok">
        <Mark tone="ok" /> {text}
    </div>
);

/**
 * Relatie kiezen in the v2 style. The accounts Exact has, the best match first, with which fields agree; the chosen
 * one field by field next to the Chargebee customer. Nothing happens until someone chose an account and ticked that it
 * is the same customer; the server checks the choice against Exact again before it relinks.
 */
export const ChooseAccountDialog = ({ axios, issue, kind, onClose, onDone }: DialogProps) => {
    const [review, setReview] = useState<RelinkReview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [chosen, setChosen] = useState<string | null>(null);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [searching, setSearching] = useState(false);
    const [query, setQuery] = useState('');
    const [result, setResult] = useState<string | null>(null);

    const load = useCallback(
        (search?: string) => {
            setSearching(true);
            setError(null);
            getRelinkReview(axios, issue.customer, issue.environment, issue.key, search)
                .then((loaded) => {
                    setReview(loaded);
                    setVerified(false);
                    setChosen(suggestedAccount(loaded));
                })
                .catch((ex) => setError(describeError(ex)))
                .finally(() => setSearching(false));
        },
        [axios, issue.customer, issue.environment, issue.key]
    );

    useEffect(() => load(), [load]);

    const candidates = useMemo(() => (review ? rankedCandidates(review) : []), [review]);
    const picked = candidates.find((c) => c.id === chosen) ?? null;
    const canChoose = review?.current?.exists !== true && !result;
    const customer = review?.chargebee;

    const search = (event: FormEvent) => {
        event.preventDefault();
        load(query.trim() || undefined);
    };

    const confirm = () => {
        if (!chosen) return;
        setBusy(true);
        setError(null);
        confirmRelink(axios, issue.customer, issue.environment, issue.key, chosen, review?.query)
            .then((outcome) => {
                setResult(`${RELINK_OUTCOME[outcome.state] ?? outcome.state}${outcome.note ? ` ${outcome.note}` : ''}`);
                onDone();
            })
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setBusy(false));
    };

    const footer = (
        <>
            <button className="btn ghost" onClick={onClose}>{result ? 'Sluiten' : 'Annuleren'}</button>
            {!result && (
                <button className="btn pri" disabled={!chosen || !verified || busy} onClick={confirm}>
                    {busy && <Spinner />}
                    Omkoppelen en opnieuw aanbieden
                </button>
            )}
        </>
    );

    return (
        <Frame title="Relatie kiezen" issue={issue} kind={kind} onClose={onClose} footer={footer}>
            {!review && !error && <Loading>Klant en relaties ophalen…</Loading>}
            {error && <div className="notice">{error}</div>}

            {review && (
                <>
                    <Route
                        from={{
                            label: 'Chargebee-klant',
                            title: customer?.exists ? customer.company || customer.name || customer.id : 'Onbekend in Chargebee',
                            sub: customer?.id,
                        }}
                        to={{
                            label: 'Nu gekoppeld in Exact',
                            title: review.current?.code ? `Relatie ${review.current.code.trim()}` : 'Geen relatie',
                            sub: review.current?.exists ? review.current.name : 'Bestaat niet meer',
                            gone: !review.current?.exists,
                        }}
                    />

                    {canChoose && (
                        <form className="hrow" onSubmit={search}>
                            <label className="search" style={{ flex: 1 }}>
                                <span aria-hidden="true">⌕</span>
                                <input
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Zoek in Exact op naam, code, e-mail of btw-nummer"
                                    aria-label="Zoek een relatie in Exact"
                                />
                            </label>
                            <button type="submit" className="btn" disabled={searching}>
                                {searching && <Spinner />}
                                Zoeken
                            </button>
                        </form>
                    )}

                    {candidates.length > 0 && (
                        <div className="xc-body">
                            <div className="xc-list" role="radiogroup" aria-label="Relaties in Exact">
                                <div className="eyebrow">Relaties in Exact · {candidates.length}</div>
                                {candidates.map((account) => (
                                    <AccountOption
                                        key={account.id ?? account.code}
                                        account={account}
                                        customer={customer}
                                        chosen={account.id === chosen}
                                        proposed={account.id === review.proposedId}
                                        disabled={!canChoose}
                                        onChoose={() => {
                                            setChosen(account.id ?? null);
                                            setVerified(false);
                                        }}
                                    />
                                ))}
                            </div>
                            <div className="card xc-compare">
                                {picked ? (
                                    <CompareTable exactLabel={`Exact ${picked.code?.trim() ?? ''}`} fields={relinkFields(customer, picked)} />
                                ) : (
                                    <div className="empty">Kies links een relatie</div>
                                )}
                            </div>
                        </div>
                    )}

                    {review.problem && <div className="xc-note">{review.problem}</div>}

                    {result ? (
                        <Done text={result} />
                    ) : (
                        picked && (
                            <Verify checked={verified} onChange={setVerified}>
                                Gecontroleerd: <strong>{picked.name ?? `relatie ${picked.code?.trim()}`}</strong> is dezelfde klant
                            </Verify>
                        )
                    )}
                </>
            )}
        </Frame>
    );
};

/** One account to choose: name and code, which fields agree, and how many. */
const AccountOption = ({
    account,
    customer,
    chosen,
    proposed,
    disabled,
    onChoose,
}: {
    account: RelinkAccount;
    customer?: RelinkChargebeeCustomer | null;
    chosen: boolean;
    proposed: boolean;
    disabled: boolean;
    onChoose: () => void;
}) => {
    const fields = relinkFields(customer, account).filter((f) => f.match !== null);
    const matches = fields.filter((f) => f.match);
    const score = matches.length >= 2 ? 'ok' : matches.length === 1 ? 'warn' : '';

    return (
        <label className={`xc-opt${chosen ? ' on' : ''}${disabled ? ' off' : ''}`}>
            <input type="radio" name="relink-account" checked={chosen} onChange={onChoose} disabled={disabled} />
            <span className="xc-opt-main">
                <span className="hrow" style={{ gap: 6 }}>
                    <strong>{account.name ?? 'Zonder naam'}</strong>
                    {account.code && <span className="mono mut">{account.code.trim()}</span>}
                    {account.blocked && <span className="chip warn">geblokkeerd</span>}
                    {proposed && <span className="chip info">voorstel scanner</span>}
                </span>
                <span className="xc-matches">
                    {matches.length > 0 ? (
                        matches.map((f) => (
                            <span key={f.label} className="chip ok">
                                ✓ {f.label}
                            </span>
                        ))
                    ) : (
                        <span className="mut">Niets komt overeen</span>
                    )}
                </span>
                {account.found && <span className="mut" style={{ fontSize: 12 }}>{account.found}</span>}
            </span>
            <span className={`chip mono ${score}`} title="Velden die overeenkomen">
                {matches.length}/{fields.length}
            </span>
        </label>
    );
};

/**
 * Koppelen in the v2 style: tie the Chargebee document to the booking Exact has of it already. The booking field by
 * field next to the document; nothing happens until someone ticked that it is the same document, and the server reads
 * Exact and the relation again before it writes.
 */
export const LinkBookingDialog = ({ axios, issue, kind, onClose, onDone }: DialogProps) => {
    const [review, setReview] = useState<LinkReview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<string | null>(null);

    useEffect(() => {
        getLinkReview(axios, issue.customer, issue.environment, issue.key)
            .then(setReview)
            .catch((ex) => setError(describeError(ex)));
    }, [axios, issue.customer, issue.environment, issue.key]);

    const entry = linkableEntry(review);
    const fields = entry ? linkFields(review?.chargebee, entry) : [];
    const differs = fields.some((f) => f.match === false);

    const confirm = () => {
        if (!entry?.entryId) return;
        setBusy(true);
        setError(null);
        confirmLink(axios, issue.customer, issue.environment, issue.key, entry.entryId)
            .then((outcome) => {
                setResult(`${LINK_OUTCOME[outcome.state] ?? outcome.state}${outcome.note ? ` ${outcome.note}` : ''}`);
                onDone();
            })
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setBusy(false));
    };

    const footer = (
        <>
            <button className="btn ghost" onClick={onClose}>{result ? 'Sluiten' : 'Annuleren'}</button>
            {!result && (
                <button className="btn pri" disabled={!entry || !verified || busy} onClick={confirm}>
                    {busy && <Spinner />}
                    Koppelen
                </button>
            )}
        </>
    );

    return (
        <Frame title="Koppelen aan de boeking in Exact" issue={issue} kind={kind} onClose={onClose} footer={footer}>
            {!review && !error && <Loading>Document en boekingen ophalen…</Loading>}
            {error && <div className="notice">{error}</div>}

            {review && (
                <>
                    <Route
                        from={{
                            label: 'Chargebee',
                            title: review.object,
                            sub: review.chargebee?.exists ? money(review.chargebee.total, review.chargebee.currency) : 'Onbekend in Chargebee',
                        }}
                        to={{
                            label: 'Al in Exact',
                            title: entry ? `Boekstuk ${entry.entryNumber ?? '?'}` : `${review.entries.length} boekingen`,
                            sub: 'Niet bekend bij de connector',
                            gone: !entry,
                        }}
                    />

                    {review.problem && <div className="xc-note">{review.problem}</div>}

                    {entry && (
                        <div className="card">
                            <CompareTable exactLabel={`Exact boekstuk ${entry.entryNumber ?? ''}`} fields={fields} />
                        </div>
                    )}

                    {entry && differs && !result && <div className="xc-note">Niet alles komt overeen. Alleen koppelen als het zeker dezelfde boeking is.</div>}

                    {result ? (
                        <Done text={result} />
                    ) : (
                        entry && (
                            <Verify checked={verified} onChange={setVerified}>
                                Gecontroleerd: <strong>boekstuk {entry.entryNumber}</strong> is {review.object}
                            </Verify>
                        )
                    )}
                </>
            )}
        </Frame>
    );
};
